import api from '../../../lib/api';
import type {
  CreateInstagramChannelRequest,
  InstagramChannel,
  InstagramUserWhitelistEntry,
  UpdateMetaDmChannelRequest,
} from '@workspace/types';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export const instagramAdminApi = {
  async getChannelByInstanceBot(instanceBotId: string): Promise<InstagramChannel | null> {
    const channels = await this.listChannels();
    return channels.find(channel => channel.instanceBotId === instanceBotId) ?? null;
  },

  async listChannels(): Promise<InstagramChannel[]> {
    const response = await api.get<ApiResponse<InstagramChannel[]>>('/admin/instagram/channels');
    return requireData(response, 'No se pudieron cargar los canales Instagram');
  },

  async createChannel(payload: CreateInstagramChannelRequest): Promise<InstagramChannel> {
    const response = await api.post<ApiResponse<InstagramChannel>>(
      '/admin/instagram/channels',
      payload,
    );
    return requireData(response, 'No se pudo crear el canal Instagram');
  },

  async updateChannel(id: string, payload: UpdateMetaDmChannelRequest): Promise<InstagramChannel> {
    const response = await api.patch<ApiResponse<InstagramChannel>>(
      `/admin/instagram/channels/${id}`,
      payload,
    );
    return requireData(response, 'No se pudo actualizar el canal Instagram');
  },

  async deleteChannel(id: string): Promise<void> {
    await api.delete(`/admin/instagram/channels/${id}`);
  },

  async listWhitelist(channelId: string): Promise<InstagramUserWhitelistEntry[]> {
    const response = await api.get<ApiResponse<InstagramUserWhitelistEntry[]>>(
      `/admin/instagram/channels/${channelId}/whitelist`,
    );
    return requireData(response, 'No se pudo cargar la whitelist');
  },

  async addUser(
    channelId: string,
    igsid: string,
    label?: string,
  ): Promise<InstagramUserWhitelistEntry> {
    const response = await api.post<ApiResponse<InstagramUserWhitelistEntry>>(
      `/admin/instagram/channels/${channelId}/whitelist`,
      { igsid, label },
    );
    return requireData(response, 'No se pudo agregar el usuario');
  },

  async removeUser(channelId: string, userId: string): Promise<void> {
    await api.delete(
      `/admin/instagram/channels/${channelId}/whitelist/${encodeURIComponent(userId)}`,
    );
  },
};
